import type { ListBuilderConfiguration } from '../types';

/**
 * Payment Methods list configuration
 * Converted from JSON to TypeScript for type safety
 */
export const PAYMENT_METHODS_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-payment-methods',
			cacheTTL: 600000,
			name: 'Payment Methods',
			type: 'default',
			active: true,
			columns: [
				{
					id: 'payment-method-type',
					headers: [
						{
							id: 'payment-method-type-header',
							text: 'Payment Method',
						},
					],
					fields: [
						{
							id: 'payment-type-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'paymentSubtype',
									objectTable: 'client_payment_method',
								},
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-1',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							displayStyle: 'line_break',
						},
						{
							// PD-16214 — the specific type under the brand: "Credit
							// Card" / "Debit Card" (the card's funding type, which used
							// to sit in its own Card Type column — PD-15154), the base
							// type for a card not yet classified, "ACH" for a bank
							// account. Resolved server-side as `paymentTypeLabel`.
							id: 'payment-base-type-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'paymentTypeLabel',
									objectTable: 'client_payment_method',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'theme',
									themeColor: 'textSecondary',
								},
							},
							displayStyle: 'text',
						},
					],
					sortable: true,
				},
				{
					// PD-16214 — the account details stack under the name: the
					// masked account number, then the routing number for a bank
					// account (hidden for cards, which have none).
					id: 'payment-name',
					headers: [
						{
							id: 'payment-name-header',
							text: 'Name',
						},
					],
					fields: [
						{
							id: 'payment-name-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'paymentName',
									objectTable: 'client_payment_method',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-name-1',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							displayStyle: 'line_break',
						},
						{
							id: 'last-four-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'paymentLastFour',
									objectTable: 'client_payment_method',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'theme',
									themeColor: 'textSecondary',
								},
							},
							displayStyle: 'text',
							prefix: '****',
						},
						{
							id: 'line-break-name-2',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							displayStyle: 'line_break',
							conditionalVisibility: {
								field: 'paymentRoutingNumber',
								condition: 'is_not_null',
							},
						},
						{
							id: 'routing-number-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'paymentRoutingNumber',
									objectTable: 'client_payment_method',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'theme',
									themeColor: 'textSecondary',
								},
							},
							displayStyle: 'text',
							prefix: 'Routing ',
							conditionalVisibility: {
								field: 'paymentRoutingNumber',
								condition: 'is_not_null',
							},
						},
					],
					sortable: true,
				},
				{
					id: 'expiration-date',
					headers: [
						{
							id: 'expiration-date-header',
							text: 'Expiration Date',
						},
					],
					fields: [
						{
							id: 'expiration-date-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'paymentExpirationDate',
									objectTable: 'client_payment_method',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
								formatOptions: {
									dateFormat: 'month-year',
								},
							},
							displayStyle: 'date',
						},
					],
					sortable: true,
				},
				{
					id: 'billing-address',
					headers: [
						{
							id: 'billing-address-header',
							text: 'Billing Address',
						},
					],
					fields: [
						{
							id: 'address1-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'paymentAddress1',
									objectTable: 'client_payment_method',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-2',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							displayStyle: 'line_break',
						},
						{
							id: 'city-state-zip-field',
							dataConfig: {
								type: 'template',
								template: '{paymentCity}, {paymentState} {paymentZip}',
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'theme',
									themeColor: 'textSecondary',
								},
							},
							displayStyle: 'text',
						},
					],
					sortable: false,
				},
				{
					id: 'actions',
					headers: [
						{
							id: 'actions-header',
							text: '',
						},
					],
					fields: [
						{
							id: 'actions-dropdown',
							dataConfig: {
								type: 'custom_text',
								customText: '',
							},
							styleConfig: {
								size: 'md',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'actions_dropdown',
							dropdownActions: [
								// PD-14226: the "Set as Primary" dropdown action was removed
								// — it duplicated the yellow star injected column on the left
								// of each row (PRIMARY_PAYMENT_METHOD_COLUMN), which is the
								// canonical way to set the primary/default method.
								{
									id: 'edit-action',
									label: 'Edit',
									iconName: 'pen',
									callbackName: 'editPaymentMethod',
									visibilityCallback: 'shouldShowEditButton',
									variant: 'primary',
									visibility: 'visible',
									requiredPermission: 'clients:payment_methods:update',
								},
								{
									id: 'delete-action',
									label: 'Delete',
									iconName: 'trash',
									callbackName: 'deletePaymentMethod',
									variant: 'danger',
									visibility: 'visible',
									requiredPermission: 'clients:payment_methods:delete',
								},
							],
						},
					],
					width: 80,
					align: 'center',
					sortable: false,
				},
			],
			defaultSortId: 'payment-method-type-asc',
			sortOrder: 0,
		},
	],
	// Root-level tableSorts (TableSortOption name/fields) so defaultSortId
	// resolves — a list-level `sortFields` is not read by the resolver (PD-14536).
	tableSorts: [
		{
			id: 'payment-method-type-asc',
			name: 'Type A → Z',
			fields: [
				{
					objectTable: '_client_payment_method',
					fieldName: 'paymentType',
					direction: 'ASC',
				},
			],
		},
	],
	paging: {
		defaultPageSize: 20,
		pageSizeOptions: [10, 20, 50],
	},
	filters: {
		activeFilters: ['paymentMethodKeywords', 'paymentMethodType'],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/clients/:clientId/payment-methods/:id',
			objectLabel: 'Payment Method',
			objectTable: '_client_payment_method',
			selectedFields: [
				{ fieldName: 'id', fieldLabel: 'ID' },
				{ fieldName: 'clientId', fieldLabel: 'Client ID' },
				{ fieldName: 'paymentType', fieldLabel: 'Payment Type' },
				{ fieldName: 'paymentSubtype', fieldLabel: 'Payment Subtype' },
				{ fieldName: 'paymentLastFour', fieldLabel: 'Last Four' },
				{ fieldName: 'paymentRoutingNumber', fieldLabel: 'Routing Number' },
				{ fieldName: 'paymentName', fieldLabel: 'Name' },
				{ fieldName: 'paymentAddress1', fieldLabel: 'Address 1' },
				{ fieldName: 'paymentAddress2', fieldLabel: 'Address 2' },
				{ fieldName: 'paymentCity', fieldLabel: 'City' },
				{ fieldName: 'paymentState', fieldLabel: 'State' },
				{ fieldName: 'paymentZip', fieldLabel: 'Zip' },
				{ fieldName: 'paymentCountry', fieldLabel: 'Country' },
				{ fieldName: 'paymentIsPrimary', fieldLabel: 'Is Primary' },
				{ fieldName: 'paymentExpirationDate', fieldLabel: 'Expiration Date' },
				{ fieldName: 'createdDate', fieldLabel: 'Created Date' },
				{ fieldName: 'modifiedDate', fieldLabel: 'Modified Date' },
			],
		},
	],
};
